/**
 * FE G1 — handle backend concurrency / DB pool gate (HTTP 503).
 *
 * Backend:
 * - ConcurrencyLimitMiddleware → errors: ["concurrency_limit"], Retry-After: 1
 * - QueuePool timeout → errors: ["db_pool_timeout"], Retry-After: 2
 *
 * Policy: honor Retry-After (+ small jitter), one silent retry for safe methods only.
 * Never tight-loop retry (avoids self-DoS under load).
 */

export const CONCURRENCY_LIMIT_ERROR = "concurrency_limit";
export const DB_POOL_TIMEOUT_ERROR = "db_pool_timeout";

const GATE_ERRORS = new Set([CONCURRENCY_LIMIT_ERROR, DB_POOL_TIMEOUT_ERROR]);

export type GateErrorCode = typeof CONCURRENCY_LIMIT_ERROR | typeof DB_POOL_TIMEOUT_ERROR;

export function isGateErrorBody(data: unknown): data is { errors: string[] } {
  if (!data || typeof data !== "object") return false;
  const errors = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((e) => typeof e === "string" && GATE_ERRORS.has(e));
}

export function isConcurrencyGateResponse(status: number, data: unknown): boolean {
  return status === 503 && isGateErrorBody(data);
}

/** Parse Retry-After header (seconds or HTTP-date). Fallback in seconds. */
export function parseRetryAfterMs(header: string | null, fallbackSeconds: number): number {
  const fallbackMs = Math.max(0, fallbackSeconds) * 1000;
  if (!header) return fallbackMs;
  const asInt = Number.parseInt(header.trim(), 10);
  if (!Number.isNaN(asInt) && String(asInt) === header.trim()) {
    return Math.max(0, asInt) * 1000;
  }
  const asDate = Date.parse(header);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, asDate - Date.now());
  }
  return fallbackMs;
}

export function retryAfterMsForGate(response: Response, data: unknown): number {
  const errors = isGateErrorBody(data) ? data.errors : [];
  const fallbackSeconds = errors.includes(DB_POOL_TIMEOUT_ERROR) ? 2 : 1;
  const header = response.headers?.get?.("Retry-After") ?? null;
  return parseRetryAfterMs(header, fallbackSeconds);
}

/** Safe idempotent methods — auto-retry once under gate. */
export function isIdempotentMethod(method: string | undefined): boolean {
  const m = (method ?? "GET").toUpperCase();
  return m === "GET" || m === "HEAD" || m === "OPTIONS";
}

/** Delay with up to ~25% jitter (still bounded). */
export function gateRetryDelayMs(baseMs: number, random: () => number = Math.random): number {
  const jitter = Math.floor(baseMs * 0.25 * random());
  return baseMs + jitter;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
